import React, { useMemo, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { OverviewTab } from "@/components/analytics/OverviewTab";
import { RevenueTab } from "@/components/analytics/RevenueTab";
import { SubscriptionTab } from "@/components/analytics/SubscriptionTab";
import { ReferralCollaborationTab } from "@/components/analytics/ReferralCollaborationTab";
import { DateRangeFilter } from "@/components/analytics/DateRangeFilter";
import { DateRange } from "@/api/business-analytics";
import { Download, RefreshCw, BarChart2 } from "lucide-react";

const BusinessAnalyticsDashboard = () => {
  // Initialize with current month as default (Revenue tab)
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: startOfMonth, to: endOfMonth };
  });

  // Separate date range for subscriptions
  const [subscriptionRange, setSubscriptionRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: startOfMonth, to: endOfMonth };
  });

  // Separate date range for referrals
  const [referralRange, setReferralRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: startOfMonth, to: endOfMonth };
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<
    "overview" | "revenue" | "subscriptions" | "referrals"
  >("overview");

  const lastUpdated = new Date().toLocaleTimeString();

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
  };

  const handleSubscriptionRangeChange = (newDateRange: DateRange) => {
    setSubscriptionRange(newDateRange);
  };

  const handleReferralRangeChange = (newDateRange: DateRange) => {
    setReferralRange(newDateRange);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export functionality to be implemented");
  };

  return (
    <AdminLayout title="Business Analytics">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Business Analytics</h1>
            <p className="text-muted-foreground">
              Comprehensive business intelligence and performance metrics
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-primary" />
            <span className="text-sm text-muted-foreground">
              Last updated: {lastUpdated}
            </span>
            {/* <Button size="sm" variant="outline" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button> */}
          </div>
        </div>

        {/* Analytics Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          className="space-y-6"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="referrals">
              Referrals & Collaboration
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <OverviewTab key={`overview-${refreshKey}`} />
          </TabsContent>

          <TabsContent value="revenue" className="space-y-6">
            <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border">
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={(range) => {
                  setDateRange(range);
                  setActiveTab("revenue");
                }}
              />
            </div>
            <RevenueTab key={`revenue-${refreshKey}`} dateRange={dateRange} />
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-6">
            <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border">
              <DateRangeFilter
                dateRange={subscriptionRange}
                onDateRangeChange={(range) => {
                  setSubscriptionRange(range);
                  setActiveTab("subscriptions");
                }}
              />
            </div>
            <SubscriptionTab
              key={`subscriptions-${refreshKey}`}
              dateRange={subscriptionRange}
            />
          </TabsContent>

          <TabsContent value="referrals" className="space-y-6">
            <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border">
              <DateRangeFilter
                dateRange={referralRange}
                onDateRangeChange={(range) => {
                  setReferralRange(range);
                  setActiveTab("referrals");
                }}
              />
            </div>
            <ReferralCollaborationTab
              key={`referrals-${refreshKey}`}
              dateRange={referralRange}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default BusinessAnalyticsDashboard;
